import _get from 'lodash.get';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import InViewObserver from 'components/InViewObserver';
import analyticsWrapper from 'helpers/analytics/analyticsWrapper';
import PropTypes from 'prop-types';
import s from './AffiliatesContent.scss';
import { AFFILIATES_SDK_URL } from '../../constants';
import { getPaidReferrer } from '../../routes/articleshow_v2/utils/mspUtils';

const getReferrer = isOrganic => {
  const paidReferrer = getPaidReferrer();
  if (paidReferrer) {
    return paidReferrer;
  }
  if (isOrganic) {
    return _get(
      typeof window !== 'undefined' ? window : {},
      'document.referrer',
      '',
    );
  }
  return '';
};

const getFeedPlatform = (isMobile, frmApp) => {
  let platform = 'web';

  if (isMobile) {
    platform = 'mweb';
    if (frmApp) {
      platform = 'app';
    }
  } else if (frmApp) {
    platform = 'webapp';
  }

  return platform;
};

const scriptPromises = {};

const loadScript = () => {
  let scriptPromise = scriptPromises[AFFILIATES_SDK_URL];

  if (scriptPromise) {
    return scriptPromise;
  }

  if (window.til_affiliates_sdk) {
    scriptPromises[AFFILIATES_SDK_URL] = Promise.resolve();
    return scriptPromises[AFFILIATES_SDK_URL];
  }

  scriptPromise = new Promise((resolve, reject) => {
    let script = document.getElementById(AFFILIATES_SDK_URL);

    if (!script) {
      script = document.createElement('script');
      script.src = AFFILIATES_SDK_URL;
      script.id = AFFILIATES_SDK_URL;
      document.body.appendChild(script);
    }

    script.addEventListener('load', () => {
      setTimeout(() => {
        resolve();
      });
    });
    script.addEventListener('error', e => {
      reject(e);
    });
  });

  scriptPromises[AFFILIATES_SDK_URL] = scriptPromise;
  return scriptPromise;
};

const AffiliatesContent = props => {
  const {
    type,
    id,
    filters,
    data,
    isMobile,
    isPrime,
    isFrmapp,
    className,
    loadingComponent,
    componentProps,
    viewMargin,
    onContentLoaded,
    noShowCallback,
    inViewClass,
    handleOrganic,
    handlePaidTraffic,
    enableWidgetsOnLoad,
    initOnPageLoad,
    affiliateCountry,
    isSaleLive,
    minHeight,
    ga4Type,
    addCityInfilter,
  } = props;

  if (!type || !(id || data || (filters && filters.pagename))) {
    return null;
  }

  const containerRef = useRef();
  const [isLoading, setIsLoading] = useState(true);
  const [errorOccurred, setErrorOccurred] = useState(false);
  const [geoInfo, setGeoInfo] = useState({});
  const [inViewCallbackReceived, setInViewCallbackReceived] = useState(false);
  const [SDKScriptLoadPromise, setSDKScriptLoadPromise] = useState();
  const [enableAffiliateWidgets, setEnableAffiliateWidgets] = useState(
    enableWidgetsOnLoad || isSaleLive,
  );

  const loadSDKAndBindEvents = () => {
    // bind to gutter ad show/hide events and
    // trigger library's corresponding size changed event
    // this is being done here, as this needs to be done only once
    // for all affiliate content widgets on the page
    window.addEventListener('GUTTER_AD_TYPE_HANDLING', () => {
      window.dispatchEvent(new Event('AFFILIATES_CONTENT_SIZE_CHANGED'));
    });
    window.addEventListener('GUTTER_CLOSE_AD_TYPE_HANDLING', () => {
      window.dispatchEvent(new Event('AFFILIATES_CONTENT_SIZE_CHANGED'));
    });

    setSDKScriptLoadPromise(loadScript());
  };

  const getCity = () => {
    if (typeof window !== 'undefined') {
      const { geoinfo: { city = '' } = {} } = window;
      return city.toLowerCase();
    }
    return '';
  };

  const getOptionsFromProps = () => {
    const options = {
      type,
    };

    if (id) {
      options.id = id;
    } else if (data) {
      options.widgetFeed = data;
    } else {
      options.filters = {
        website: 'toi',
        platform: getFeedPlatform(isMobile, isFrmapp),
        country: geoInfo.country,
        city: addCityInfilter ? getCity() : '',
        affiliateCountry,
        pcheck: isPrime,
        ...filters,
      };
      if (handlePaidTraffic) {
        options.filters.referrer = getReferrer(handleOrganic);
      }
    }

    if (className) {
      options.className = className;
    }

    if (componentProps) {
      options.props = componentProps;
    }

    return options;
  };

  const loadWidget = () => {
    window.til_affiliates_sdk
      .loadWidget(containerRef.current, getOptionsFromProps())
      .then(response => {
        onContentLoaded(response);
        setIsLoading(false);
      })
      .catch(() => {
        noShowCallback();
        setIsLoading(false);
        setErrorOccurred(true);
      });
  };

  const loadContent = () => {
    SDKScriptLoadPromise.then(() => {
      if (window.til_affiliates_sdk) {
        loadWidget();
      } else {
        setErrorOccurred(true);
      }
    }).catch(() => {
      setErrorOccurred(true);
    });
  };

  const scrollHandler = useCallback(() => {
    if (window.scrollY > 200) {
      setEnableAffiliateWidgets(true);
      window.removeEventListener('scroll', scrollHandler);
    }
  }, []);

  useEffect(() => {
    if (
      window.TimesGDPR &&
      window.TimesGDPR.common.consentModule.gdprCallback
    ) {
      window.TimesGDPR.common.consentModule.gdprCallback(dataObj => {
        // we are doing this if country is not present in gdpr, read it from window.geoinfo
        // eslint-disable-next-line camelcase
        const { region_code = '', Continent = '', CountryCode = '' } =
          window.geoinfo || {};

        setGeoInfo({
          country: dataObj.userCountry || CountryCode.toLowerCase(),
          continent: dataObj.userContinent || Continent.toLowerCase(),
          region: dataObj.userRegion || region_code.toLowerCase(),
        });
      });
    }
    if (!enableWidgetsOnLoad && !isSaleLive) {
      window.addEventListener('scroll', scrollHandler);
    }
  }, []);

  useEffect(
    () => {
      if (initOnPageLoad) {
        setInViewCallbackReceived(true);
      }
      if (enableAffiliateWidgets) {
        loadSDKAndBindEvents();
      }
    },
    [enableAffiliateWidgets],
  );

  useEffect(
    () => {
      if (geoInfo.country && inViewCallbackReceived && SDKScriptLoadPromise) {
        loadContent();
      }
    },
    [geoInfo.country, inViewCallbackReceived, SDKScriptLoadPromise],
  );

  if (errorOccurred) {
    return null;
  }

  return (
    <InViewObserver
      inViewCallback={() => {
        setInViewCallbackReceived(true);
        if (ga4Type) {
          analyticsWrapper('ga4', {
            event: 'user_shown',
            type: `${ga4Type}`,
          });
        }
      }}
      viewMargin={viewMargin || '1000px 0px 1000px 0px'}
      className={inViewClass}
    >
      {isLoading && React.isValidElement(loadingComponent) && loadingComponent}
      <div
        ref={containerRef}
        data-aff-type="aff-content"
        data-aff-version="v1"
        style={{ minHeight: minHeight || '' }}
        className={
          isLoading && !React.isValidElement(loadingComponent)
            ? s.aff_loading
            : ''
        }
      />
    </InViewObserver>
  );
};

AffiliatesContent.propTypes = {
  type: PropTypes.string.isRequired,
  id: PropTypes.string,
  filters: PropTypes.shape({
    // website: PropTypes.string,  -- set as toi hard coded
    // platform: PropTypes.string, -- identified based on isMobile and isFrmapp
    // pcheck: PropTypes.string, -- identified based on isPrime
    // country: PropTypes.string, -- identified from gdpr callback
    pagename: PropTypes.string,
    version: PropTypes.string,
    sectionl1: PropTypes.string,
    keywords: PropTypes.string,
    lang: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // productId and affiliateName are for individual product card widgets
    productId: PropTypes.string,
    affiliateName: PropTypes.string,
    imgid: PropTypes.string,
  }),
  data: PropTypes.shape({}),
  isMobile: PropTypes.bool,
  isPrime: PropTypes.bool,
  isFrmapp: PropTypes.bool,
  handleOrganic: PropTypes.bool,
  handlePaidTraffic: PropTypes.bool,
  className: PropTypes.string,
  inViewClass: PropTypes.string,
  viewMargin: PropTypes.string,
  loadingComponent: PropTypes.node,
  componentProps: PropTypes.shape({}),
  onContentLoaded: PropTypes.func,
  noShowCallback: PropTypes.func,
  enableWidgetsOnLoad: PropTypes.bool,
  initOnPageLoad: PropTypes.bool,
  ga4Type: PropTypes.string,
};

AffiliatesContent.defaultProps = {
  id: undefined,
  filters: undefined,
  data: undefined,
  isMobile: false,
  isPrime: false,
  isFrmapp: false,
  handleOrganic: false,
  handlePaidTraffic: false,
  className: '',
  inViewClass: '',
  loadingComponent: undefined,
  componentProps: undefined,
  viewMargin: '',
  onContentLoaded: () => {},
  noShowCallback: () => {},
  enableWidgetsOnLoad: false,
  initOnPageLoad: false,
  ga4Type: '',
};

const mapStateToProps = state => ({
  isPrime: state.isPrime,
  isMobile: state.isMobile,
  isFrmapp: state.isFrmapp,
  isSaleLive:
    _get(
      state,
      'common.siteConfig.affiliates.saleStatus.isIndiaSaleLive',
      'false',
    ) === 'true',
});

export default connect(mapStateToProps)(AffiliatesContent);
